/**
 * 后台 API 客户端（/admin-api/v1）：后台页面统一用这里的 `adminApi`，不要自己调用 fetch。
 *
 * - 自动带 `X-Nexora: 1`；错误统一为 ApiError（code / params），文案用 useAdminErrorText() 映射到 `error.<CODE>` 词条；
 * - 401（ADMIN_AUTH_REQUIRED）默认回到登录页 `/login?returnTo=<当前路径>`（登录、激活、重置页面本身不跳转）；
 *   路由由 AdminApp 绑定（bindAdminApiRouter），站内跳转不刷新页面；
 * - 423（ADMIN_SESSION_LOCKED）与 503（ADMIN_READONLY_MODE）由 AdminLayout 订阅（锁屏遮罩、只读提示条）；
 * - 轮询类请求传 `{ passive: true }`（带 X-Nexora-Activity: passive，不刷新 30 分钟无操作计时，spec-core §1.13 H6）；
 * - 资金复核等写操作传 `{ idempotencyKey }`。
 */
import { ADMIN_API_PREFIX } from '@nexora/shared';
import { createApiClient, type ApiClient } from '@nexora/ui';

export interface RouterLike {
  state: { location: { pathname: string; search: string; hash: string } };
  navigate: (to: string, options?: { replace?: boolean }) => unknown;
}

let boundRouter: RouterLike | null = null;

/** 绑定当前路由（AdminApp 挂载时调用），返回解绑函数。 */
export function bindAdminApiRouter(router: RouterLike): () => void {
  boundRouter = router;
  return () => {
    if (boundRouter === router) boundRouter = null;
  };
}

export const ADMIN_LOGIN_PATH = '/login';
/** 不需要会话的页面（登录、激活、重置，spec-identity PG-X01、PG-X02） */
export const ADMIN_PUBLIC_PATHS: readonly string[] = ['/login', '/activate', '/reset'];

export function adminLoginTarget(location: { pathname: string; search: string; hash?: string }): string | null {
  if (ADMIN_PUBLIC_PATHS.includes(location.pathname)) return null;
  const back = `${location.pathname}${location.search}${location.hash ?? ''}`;
  return back === '/' ? ADMIN_LOGIN_PATH : `${ADMIN_LOGIN_PATH}?returnTo=${encodeURIComponent(back)}`;
}

/**
 * 后台会话失效的错误码：只有它回到登录页。本人 TOTP 复核（ADMIN_TOTP_REQUIRED / ADMIN_TOTP_INVALID）与登录失败
 * （STAFF_LOGIN_FAILED）等 401 由页面就地处理（INTEGRATE D-INT-21）。
 */
export const ADMIN_SESSION_ERROR_CODES: readonly string[] = ['ADMIN_AUTH_REQUIRED', 'AUTH_SESSION_EXPIRED'];

export const adminApi: ApiClient = createApiClient({
  baseUrl: ADMIN_API_PREFIX,
  onUnauthorized: (error) => {
    if (!ADMIN_SESSION_ERROR_CODES.includes(error.code)) return;
    const target = adminLoginTarget(boundRouter?.state.location ?? window.location);
    if (!target) return;
    if (boundRouter) void boundRouter.navigate(target, { replace: true });
    else window.location.replace(target);
  },
});
